import React from "react";

interface TooltipProps {
  position?: string;
  active?: boolean;
  triggerElement: string;
  triggerEvent: string;
  width?: number;
  triggerDelay?: number;
  theme?: string;
  children?: React.ReactNode;
  className?: string;
  hide?: boolean;
}

interface TooltipState {
  position: string;
  active: boolean;
  target: Element | null;
  top?: number;
  left?: number;
}

const inactive = {
  top: -9999,
  left: -9999,
  active: false,
};

class Tooltip extends React.Component<TooltipProps, TooltipState> {
  selfRef: React.RefObject<HTMLDivElement>;
  /**
   * Drops every listener added in componentDidMount. The old cleanup passed
   * fresh arrow functions to removeEventListener, which removes nothing, so
   * the trigger kept calling into an unmounted tooltip.
   */
  private listeners: AbortController | null = null;
  /** The pending show after a hover; cleared on unmount so it cannot measure a gone tooltip. */
  private hoverTimer: ReturnType<typeof setTimeout> | undefined;
  constructor(props: TooltipProps) {
    super(props);
    this.selfRef = React.createRef();
    this.state = {
      position: props.position || "top",
      active: props.active || false,
      target: null,
    };
  }

  componentDidMount() {
    const targets = Array.from(
      document.querySelectorAll(this.props.triggerElement)
    );
    const { triggerEvent, triggerDelay } = this.props;
    if (targets.length === 0) return;
    // Made per mount: StrictMode mounts the same instance twice.
    this.listeners = new AbortController();
    const options = { signal: this.listeners.signal };
    if (triggerEvent === "hover") {
      targets.map((target) => {
        target.addEventListener("mouseleave", () => {
          clearTimeout(this.hoverTimer);
          setTimeout(() => {
            this.setState(inactive);
          }, 0);
        }, options);
        target.addEventListener("blur", () => {
          clearTimeout(this.hoverTimer);
          setTimeout(() => {
            this.setState(inactive);
          }, 0);
        }, options);
        target.addEventListener("click", () => {
          if (window.innerWidth < 640) return;
          clearTimeout(this.hoverTimer);
          setTimeout(() => {
            this.setState(inactive);
          }, 0);
        }, options);
        target.addEventListener("mouseenter", (e) => {
          this.hoverTimer = setTimeout(() => {
            const { top, left } = this.move(e);
            const active = {
              top,
              left,
              active: true,
            };
            this.setState(active);
          }, triggerDelay);
        }, options);
      });
    } else {
      // triggerEvent === "click"
      targets.map((target) => {
        target.addEventListener(this.props.triggerEvent, (e) => {
          const { top, left } = this.move(e);
          const active = {
            top,
            left,
            active: true,
          };
          this.setState(this.state.active ? inactive : active);
        }, options);
      });
    }
  }

  componentWillUnmount() {
    this.listeners?.abort();
    clearTimeout(this.hoverTimer);
  }

  move(_e: Event) {
    let top, left;
    const { position } = this.props;
    const target = document.querySelector(this.props.triggerElement);
    // Only called from a trigger listener, which unmount removes, so the
    // tooltip is mounted and its ref set.
    const { clientHeight, clientWidth } = this.selfRef.current;
    const { offsetTop, offsetLeft, offsetHeight, offsetWidth } = target as HTMLElement;
    switch (position) {
      case "below":
        top = offsetTop + offsetHeight + 15;
        left = offsetLeft + offsetWidth / 2 - clientWidth / 2;
        if (left < 5) {
          left = 5;
        }
        break;

      case "above":
        top = offsetTop - clientHeight - 15;
        left = offsetLeft + offsetWidth / 2 - clientWidth / 2 - 10;
        if (left < 5) left = 5;
        break;

      case "right":
        top = offsetTop + offsetHeight / 2 - clientHeight / 2;
        left = offsetLeft + offsetWidth + 25;
        if (left + offsetWidth > clientWidth)
          left = clientWidth - offsetWidth - 5;
        break;

      case "left":
        top = offsetTop + offsetHeight / 2 - clientHeight / 2;
        left = offsetLeft - clientWidth - 25;
        break;
    }
    // Above or below a control at the page's right edge (the search view
    // toggle), a centred tip ran off the screen. Pull it back in; a tip that
    // already fits is not moved.
    if ((position === "above" || position === "below") && typeof window !== "undefined") {
      const parentLeft = (this.selfRef.current.offsetParent as HTMLElement | null)?.getBoundingClientRect().left ?? 0;
      const maxLeft = window.innerWidth - parentLeft - clientWidth - 5;
      if (left > maxLeft) left = maxLeft;
    }
    return { top, left };
  }

  render() {
    const active = this.state.active ? "active" : "";
    const { top, left } = this.state;
    const { width, theme, className = "", hide } = this.props;
    let classNames = `Tooltip ${theme} ${className} `;
    const { position } = this.props;
    classNames += `${position} ${active} ${hide ? "hidden" : ""}`;
    const tooltipStyle = { top, left, width };
    return (
      <div
        className={classNames}
        ref={this.selfRef}
        style={tooltipStyle}
        onMouseLeave={() => {
          this.setState(inactive);
        }}
      >
        {this.props.children}
        <div className="cover" />
      </div>
    );
  }
}

export default Tooltip;
